import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useRef,
  useCallback,
  ReactNode,
} from 'react';
import {
  QuotaCheckResult,
  UserUsage,
  invalidateUserUsageCache,
  invalidateProfileCache,
  invalidateCategoriesCache,
  plansService,
} from '../services/supabase';
import { useAuth } from './AuthContext';
import QuotaLimitAlert from '../components/ui/QuotaLimitAlert';

interface PlanInfo {
  name: string;
  has_ai_rewrite: boolean;
}

/** Optional pending create held while the quota modal is open (projects). */
export type QuotaPendingCreate = {
  /** Close (X) or upgrade — drop the optimistic draft from UI */
  onDismiss: () => void;
  /** After a successful archive — persist the draft create */
  onSlotFreed: () => void | Promise<void>;
};

interface QuotaContextType {
  showQuotaLimit: (info: QuotaCheckResult, pending?: QuotaPendingCreate) => void;
  userPlan: PlanInfo | null;
  usage: UserUsage | null;
  usageLoading: boolean;
  refreshUsage: (options?: { force?: boolean }) => Promise<UserUsage | null>;
}

const QuotaContext = createContext<QuotaContextType | undefined>(undefined);

export const useQuota = () => {
  const context = useContext(QuotaContext);
  if (!context) {
    throw new Error('useQuota must be used within QuotaProvider');
  }
  return context;
};

const PLAN_CACHE_KEY = 'leadsme_user_plan_cache';
const PLAN_CACHE_DURATION = 5 * 60 * 1000; // 5 minutes

const getCachedPlan = (userId: string): PlanInfo | null => {
  try {
    const cached = localStorage.getItem(`${PLAN_CACHE_KEY}_${userId}`);
    if (!cached) return null;
    const { plan, timestamp } = JSON.parse(cached);
    if (Date.now() - timestamp > PLAN_CACHE_DURATION) {
      localStorage.removeItem(`${PLAN_CACHE_KEY}_${userId}`);
      return null;
    }
    return plan;
  } catch {
    return null;
  }
};

const setCachedPlan = (userId: string, plan: PlanInfo) => {
  try {
    localStorage.setItem(
      `${PLAN_CACHE_KEY}_${userId}`,
      JSON.stringify({
        plan,
        timestamp: Date.now(),
      }),
    );
  } catch {
    // Ignore localStorage errors
  }
};

function planInfoFromUsage(usage: UserUsage | null): PlanInfo | null {
  if (!usage?.plan) return null;
  return {
    name: usage.plan.name,
    has_ai_rewrite: usage.plan.has_ai_rewrite ?? false,
  };
}

export const QuotaProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const { user } = useAuth();
  const [quotaInfo, setQuotaInfo] = useState<QuotaCheckResult | null>(null);
  const [usage, setUsage] = useState<UserUsage | null>(null);
  const [userPlan, setUserPlan] = useState<PlanInfo | null>(null);
  const [usageLoading, setUsageLoading] = useState(false);
  const pendingCreateRef = useRef<QuotaPendingCreate | null>(null);

  const refreshUsage = useCallback(
    async (options?: { force?: boolean }): Promise<UserUsage | null> => {
      if (!user?.id) {
        return null;
      }

      setUsageLoading(true);
      try {
        const usageData = await plansService.getUserUsage(options);
        if (usageData) {
          setUsage(usageData);
          const planInfo = planInfoFromUsage(usageData);
          if (planInfo) {
            setUserPlan(planInfo);
            setCachedPlan(user.id, planInfo);
          }
        } else if (!options?.force) {
          const defaultPlan: PlanInfo = { name: 'free', has_ai_rewrite: false };
          setUserPlan(defaultPlan);
          setCachedPlan(user.id, defaultPlan);
        }
        return usageData;
      } catch (error) {
        console.error('Error loading user usage:', error);
        return null;
      } finally {
        setUsageLoading(false);
      }
    },
    [user?.id],
  );

  useEffect(() => {
    if (!user?.id) {
      setUsage(null);
      setUserPlan(null);
      invalidateUserUsageCache();
      invalidateProfileCache();
      invalidateCategoriesCache();
      return;
    }

    const cachedPlan = getCachedPlan(user.id);
    if (cachedPlan) {
      setUserPlan(cachedPlan);
    }

    void refreshUsage();
  }, [user?.id, refreshUsage]);

  const showQuotaLimit = (info: QuotaCheckResult, pending?: QuotaPendingCreate) => {
    if (pendingCreateRef.current && pendingCreateRef.current !== pending) {
      pendingCreateRef.current.onDismiss();
    }
    pendingCreateRef.current = pending ?? null;
    setQuotaInfo(info);
  };

  const dismissQuotaLimit = () => {
    const pending = pendingCreateRef.current;
    pendingCreateRef.current = null;
    setQuotaInfo(null);
    pending?.onDismiss();
  };

  const completeQuotaAfterSlotFreed = async () => {
    const pending = pendingCreateRef.current;
    pendingCreateRef.current = null;
    setQuotaInfo(null);
    await pending?.onSlotFreed();
    await refreshUsage({ force: true });
  };

  return (
    <QuotaContext.Provider
      value={{
        showQuotaLimit,
        userPlan,
        usage,
        usageLoading,
        refreshUsage,
      }}
    >
      {children}
      {quotaInfo && quotaInfo.resource && quotaInfo.current !== undefined && quotaInfo.limit !== null && (
        <QuotaLimitAlert
          isOpen={!!quotaInfo}
          onDismiss={dismissQuotaLimit}
          onSlotFreed={completeQuotaAfterSlotFreed}
          resource={quotaInfo.resource}
          current={quotaInfo.current}
          limit={quotaInfo.limit as number}
        />
      )}
    </QuotaContext.Provider>
  );
};
